import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import DocumentPreviewDialog from '../components/DocumentPreviewDialog';
import {
  Plus,
  Search,
  Filter,
  Check,
  X,
  Receipt,
  Upload,
  Euro,
  Download,
  ArrowUpDown,
  Calendar,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Paperclip,
  Loader2,
  Sparkles,
  Eye,
  Trash2,
  FileDown,
  AlertTriangle,
  Pencil,
  Save,
  Inbox,
  History,
  UserCheck,
  ClipboardCheck
} from 'lucide-react';
import { formatDateShort, formatCurrency, getStatusColor, getStatusLabel } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const categories = [
  { value: 'transport', label: 'Transport' },
  { value: 'repas', label: 'Repas' },
  { value: 'hebergement', label: 'Hébergement' },
  { value: 'materiel', label: 'Matériel' },
  { value: 'carburant', label: 'Carburant' },
  { value: 'peage', label: 'Péage' },
  { value: 'parking', label: 'Parking' },
  { value: 'autre', label: 'Autre' }
];

// Libellés des actions de l'historique du workflow
const ACTION_LABELS = {
  soumis: 'Soumise',
  approuve: 'Approuvée',
  approuve_auto: 'Approuvée automatiquement',
  refuse: 'Refusée',
  corrige: 'Corrigée',
  confirme: 'Confirmée',
  paye: 'Payée'
};

const ACTION_COLORS = {
  soumis: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  approuve: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  approuve_auto: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  refuse: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  corrige: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  confirme: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  paye: 'bg-[#0F5156]/10 text-[#0F5156] dark:text-emerald-200'
};

const FIELD_LABELS = {
  montant_ttc: 'Montant TTC',
  montant_ht: 'Montant HT',
  tva: 'TVA',
  categorie: 'Catégorie',
  description: 'Description'
};

const formatFieldValue = (field, value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (['montant_ttc', 'montant_ht', 'tva'].includes(field)) return formatCurrency(value);
  if (field === 'categorie') return categories.find(c => c.value === value)?.label || value;
  return String(value);
};

const formatDateTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(d);
};

// Convertit un base64 (sans préfixe) en URL blob: — les data URI PDF ne s'affichent pas dans un iframe sous Chrome/Edge
const base64ToBlobUrl = (base64, mimetype) => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: mimetype || 'application/octet-stream' }));
};

const EMPTY_FORM = {
  employee_id: '',
  montant_ttc: '',
  montant_ht: '',
  tva: '',
  categorie: '',
  description: '',
  date_depense: '',
  fournisseur: '',
  justificatif: null,
  justificatif_filename: '',
  justificatif_mimetype: '',
  justificatif_preview_url: null
};

const Expenses = () => {
  const { hasPermission } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showMine, setShowMine] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [detailExpense, setDetailExpense] = useState(null);
  const [editDetail, setEditDetail] = useState(null);
  const [savingDetail, setSavingDetail] = useState(false);
  const [actionComment, setActionComment] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewLoadingId, setPreviewLoadingId] = useState(null);
  const previewUrlRef = useRef(null);

  // Filtres avancés
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [montantMin, setMontantMin] = useState('');
  const [montantMax, setMontantMax] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Nouveau flow: étape 1 = upload, étape 2 = vérification IA
  const [step, setStep] = useState(1);
  const [extracting, setExtracting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);

  const [formData, setFormData] = useState(EMPTY_FORM);

  const canSubmit = hasPermission('expenses_submit') || hasPermission('expenses_edit');
  const canView = hasPermission('expenses_view');
  const canPay = hasPermission('expenses_pay');
  const canExport = hasPermission('expenses_export');
  const canEditAny = hasPermission('expenses_edit');

  const buildEditDetail = (expense) => ({
    id: expense.id,
    montant_ttc: expense.montant_ttc || expense.montant || '',
    montant_ht: expense.montant_ht || '',
    tva: expense.tva || '',
    categorie: expense.categorie || '',
    description: expense.description || '',
    date_depense: expense.date_depense || '',
    fournisseur: expense.fournisseur || ''
  });

  const fetchData = useCallback(async () => {
    try {
      const requests = [axios.get(`${API_URL}/expenses`)];
      if (canView || hasPermission('employees_view')) {
        requests.push(axios.get(`${API_URL}/employees`));
      }
      const results = await Promise.all(requests);
      const list = results[0].data || [];
      setExpenses(list);
      if (results[1]) setEmployees(results[1].data);
      // Re-dériver le panneau de détail depuis la liste rafraîchie (évite un panneau périmé)
      setDetailExpense(prev => {
        if (!prev) return prev;
        const fresh = list.find(e => e.id === prev.id) || null;
        if (!fresh || !fresh.can_edit) setEditDetail(null);
        return fresh;
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [canView]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Libérer l'URL blob de l'aperçu à la fermeture / au démontage
  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const releaseFormPreview = (data) => {
    if (data?.justificatif_preview_url) URL.revokeObjectURL(data.justificatif_preview_url);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Le fichier ne doit pas dépasser 10 Mo');
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format autorisé : PDF, JPG, PNG');
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => {
        releaseFormPreview(prev);
        return {
          ...prev,
          justificatif: reader.result,
          justificatif_filename: file.name,
          justificatif_mimetype: file.type,
          justificatif_preview_url: previewUrl
        };
      });
    };
    reader.readAsDataURL(file);
  };

  // Retire uniquement le fichier joint (conserve les autres champs et l'étape courante)
  const removeFile = () => {
    setFormData(prev => {
      releaseFormPreview(prev);
      return {
        ...prev,
        justificatif: null,
        justificatif_filename: '',
        justificatif_mimetype: '',
        justificatif_preview_url: null
      };
    });
  };

  const handleExtract = async () => {
    if (!formData.justificatif) {
      toast.error('Veuillez d\'abord sélectionner un fichier');
      return;
    }

    setExtracting(true);
    try {
      const response = await axios.post(`${API_URL}/expenses/extract`, {
        file: formData.justificatif,
        filename: formData.justificatif_filename,
        mimetype: formData.justificatif_mimetype
      });

      if (response.data.success) {
        const ext = response.data.extracted;
        setFormData(prev => ({
          ...prev,
          montant_ttc: ext.montant_ttc ? String(ext.montant_ttc) : prev.montant_ttc,
          montant_ht: ext.montant_ht ? String(ext.montant_ht) : prev.montant_ht,
          tva: ext.tva ? String(ext.tva) : prev.tva,
          description: ext.description || prev.description,
          date_depense: ext.date_depense || prev.date_depense,
          categorie: ext.categorie || prev.categorie,
          fournisseur: ext.fournisseur || prev.fournisseur
        }));
        toast.success('Informations extraites par l\'IA');
        setStep(2);
      } else {
        toast.error(response.data.error || 'Extraction échouée');
        setStep(2);
      }
    } catch (error) {
      console.error('Error extracting:', error);
      const detail = error.response?.data?.detail || error.response?.data?.error || 'Erreur lors de l\'extraction. Remplissez manuellement.';
      toast.error(detail);
      setStep(2);
    } finally {
      setExtracting(false);
    }
  };

  // Vérification de doublon avant soumission : retourne true si un doublon a été trouvé
  const checkDuplicates = async () => {
    if (!formData.montant_ttc || !formData.date_depense) return false;
    try {
      const response = await axios.post(`${API_URL}/expenses/check-duplicate`, {
        montant_ttc: parseFloat(formData.montant_ttc),
        date_depense: formData.date_depense,
        fournisseur: formData.fournisseur
      });
      if (response.data.has_duplicates) {
        setDuplicates(response.data.duplicates);
        setDuplicateDialogOpen(true);
        return true;
      }
    } catch (error) {
      // En cas d'erreur, ne pas bloquer la soumission
      console.error('Duplicate check failed:', error);
    }
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.justificatif) {
      toast.error('Le justificatif est obligatoire');
      return;
    }
    if (!formData.montant_ttc) {
      toast.error('Le montant TTC est obligatoire');
      return;
    }
    if (!formData.categorie) {
      toast.error('La catégorie est obligatoire');
      return;
    }
    if (!formData.description) {
      toast.error('La description est obligatoire');
      return;
    }
    if (!formData.date_depense) {
      toast.error('La date est obligatoire');
      return;
    }

    const hasDup = await checkDuplicates();
    if (hasDup) return; // La boîte de dialogue demande confirmation

    doSubmit();
  };

  const doSubmit = async () => {
    setSubmitting(true);
    setDuplicateDialogOpen(false);
    setDuplicates([]);
    try {
      const response = await axios.post(`${API_URL}/expenses`, {
        employee_id: formData.employee_id || undefined,
        montant_ttc: parseFloat(formData.montant_ttc),
        montant_ht: formData.montant_ht ? parseFloat(formData.montant_ht) : undefined,
        tva: formData.tva ? parseFloat(formData.tva) : undefined,
        categorie: formData.categorie,
        description: formData.description,
        date_depense: formData.date_depense,
        fournisseur: formData.fournisseur,
        justificatif: formData.justificatif,
        justificatif_filename: formData.justificatif_filename,
        justificatif_mimetype: formData.justificatif_mimetype
      });
      toast.success(response.data?.statut === 'approuve' ? 'Note de frais déposée et approuvée automatiquement' : 'Note de frais déposée');
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error creating expense:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la création';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const statusToast = (action, data) => {
    if (action === 'approuve' && data?.statut === 'en_attente' && data?.etape_courante_label) {
      return `Note de frais approuvée, transmise à l'étape « ${data.etape_courante_label} »`;
    }
    if (action === 'approuve' && data?.statut === 'a_confirmer') return 'Note de frais approuvée, en attente de confirmation du déposant';
    const labels = {
      approuve: 'Note de frais approuvée',
      refuse: 'Note de frais refusée',
      paye: 'Note de frais marquée comme payée',
      confirme: 'Note de frais confirmée',
      en_attente: 'Note de frais remise en attente'
    };
    return labels[action] || 'Note de frais mise à jour';
  };

  const handleStatusChange = async (id, newStatus, comment = '') => {
    setActionLoading(true);
    try {
      // Si le validateur a modifié des champs, sauvegarder d'abord
      if (editDetail && editDetail.id === id) {
        await saveDetailEdits(false);
      }
      const params = new URLSearchParams({ statut: newStatus });
      if (comment) params.set('comment', comment);
      const response = await axios.put(`${API_URL}/expenses/${id}/status?${params.toString()}`);
      toast.success(statusToast(newStatus, response.data));
      setActionComment('');
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      const msg = error.response?.data?.detail || 'Erreur lors de la mise à jour';
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const saveDetailEdits = async (showToast = true) => {
    if (!editDetail) return;
    setSavingDetail(true);
    try {
      await axios.put(`${API_URL}/expenses/${editDetail.id}`, {
        montant_ttc: parseFloat(editDetail.montant_ttc) || 0,
        montant_ht: editDetail.montant_ht ? parseFloat(editDetail.montant_ht) : null,
        tva: editDetail.tva ? parseFloat(editDetail.tva) : null,
        categorie: editDetail.categorie,
        description: editDetail.description,
        date_depense: editDetail.date_depense,
        fournisseur: editDetail.fournisseur
      });
      if (showToast) {
        toast.success('Modifications sauvegardées');
        fetchData();
      }
    } catch (error) {
      console.error('Error saving detail edits:', error);
      const msg = error.response?.data?.detail || 'Erreur lors de la sauvegarde';
      toast.error(msg);
      throw error;
    } finally {
      setSavingDetail(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette note de frais ?')) return;
    try {
      await axios.delete(`${API_URL}/expenses/${id}`);
      toast.success('Note de frais supprimée');
      if (detailExpense?.id === id) { setDetailExpense(null); setEditDetail(null); }
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de la suppression';
      toast.error(msg);
    }
  };

  const fetchJustificatif = async (expense) => {
    const response = await axios.get(`${API_URL}/expenses/${expense.id}/justificatif`);
    const { data, mimetype, filename } = response.data;
    if (!data) throw new Error('empty');
    return { url: base64ToBlobUrl(data, mimetype), mimetype, filename: filename || expense.justificatif_filename || 'justificatif' };
  };

  const closePreview = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewDoc(null);
  };

  const handlePreview = async (expense) => {
    setPreviewLoadingId(expense.id);
    try {
      const { url, mimetype, filename } = await fetchJustificatif(expense);
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = url;
      setPreviewDoc({ data: url, mimetype, filename, expense });
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      toast.error(detail ? `${detail}${status ? ` (HTTP ${status})` : ''}` : 'Impossible de charger le justificatif');
    } finally {
      setPreviewLoadingId(null);
    }
  };

  const handleDownloadJustificatif = async (expense) => {
    try {
      const { url, filename } = await fetchJustificatif(expense);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Impossible de télécharger le justificatif');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (dateDebut) params.set('periode', dateDebut.substring(0, 7));
      if (filterStatus !== 'all') params.set('statut', filterStatus);

      const response = await axios.get(`${API_URL}/expenses/export?${params.toString()}`);
      const { csv, filename, count, total_ttc } = response.data;

      // Télécharger le CSV
      const link = document.createElement('a');
      link.href = `data:text/csv;base64,${csv}`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`${count} note(s) exportée(s) - Total TTC : ${formatCurrency(total_ttc)}`);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de l\'export';
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  const resetForm = () => {
    setFormData(prev => {
      releaseFormPreview(prev);
      return EMPTY_FORM;
    });
    setStep(1);
    setExtracting(false);
    setSubmitting(false);
    setDuplicates([]);
    setDuplicateDialogOpen(false);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setShowMine(false);
    setDateDebut('');
    setDateFin('');
    setMontantMin('');
    setMontantMax('');
    setSortBy('date_desc');
  };

  const getMontant = (exp) => exp.montant_ttc || exp.montant || 0;
  const isToProcess = (exp) => !!(exp.is_assignee || exp.can_confirm);

  const filteredExpenses = expenses
    .filter(exp => {
      const matchesSearch =
        (exp.employee_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (exp.categorie || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (exp.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (exp.fournisseur || '').toLowerCase().includes(searchTerm.toLowerCase());

      const matchesFilter = filterStatus === 'all' || exp.statut === filterStatus;
      const matchesMine = !showMine || isToProcess(exp);
      const matchesDateDebut = !dateDebut || (exp.date_depense || '') >= dateDebut;
      const matchesDateFin = !dateFin || (exp.date_depense || '') <= dateFin;
      const m = getMontant(exp);
      const matchesMontantMin = !montantMin || m >= parseFloat(montantMin);
      const matchesMontantMax = !montantMax || m <= parseFloat(montantMax);

      return matchesSearch && matchesFilter && matchesMine && matchesDateDebut && matchesDateFin && matchesMontantMin && matchesMontantMax;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'date_asc': return (a.date_depense || '').localeCompare(b.date_depense || '');
        case 'date_desc': return (b.date_depense || '').localeCompare(a.date_depense || '');
        case 'montant_asc': return getMontant(a) - getMontant(b);
        case 'montant_desc': return getMontant(b) - getMontant(a);
        default: return (b.date_depense || '').localeCompare(a.date_depense || '');
      }
    });

  const toProcessCount = expenses.filter(isToProcess).length;

  const totalPending = expenses
    .filter(e => e.statut === 'en_attente' || e.statut === 'a_confirmer')
    .reduce((sum, e) => sum + getMontant(e), 0);

  const totalApproved = expenses
    .filter(e => e.statut === 'approuve')
    .reduce((sum, e) => sum + getMontant(e), 0);

  const renderFormPreview = (heightClass) => {
    if (!formData.justificatif_preview_url) return null;
    if (formData.justificatif_mimetype === 'application/pdf') {
      return (
        <iframe
          src={formData.justificatif_preview_url}
          className={`w-full ${heightClass} border-0`}
          title="Aperçu PDF"
        />
      );
    }
    return (
      <div className="p-2 flex justify-center bg-white dark:bg-slate-900">
        <img
          src={formData.justificatif_preview_url}
          alt="Aperçu justificatif"
          className={`${heightClass} object-contain rounded`}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F5156]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Notes de frais</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Déposez et suivez vos notes de frais
          </p>
        </div>
        <div className="flex gap-2">
          {canExport && (
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={exporting}
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileDown className="w-4 h-4 mr-2" />
              )}
              Exporter CSV
            </Button>
          )}
          {canSubmit && (
            <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#C27868] hover:bg-[#a85d4d]" data-testid="add-expense-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Déposer une note de frais
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {step === 1 ? 'Étape 1 : Déposer le justificatif' : 'Étape 2 : Vérifier les informations'}
                  </DialogTitle>
                </DialogHeader>

                {/* Étape 1 : Upload du fichier */}
                {step === 1 && (
                  <div className="space-y-4 py-4">
                    <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-8 text-center">
                      <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                      <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
                        Déposez votre facture, reçu ou justificatif
                      </p>
                      <Input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                        className="max-w-xs mx-auto file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#C27868] file:text-white hover:file:bg-[#a85d4d]"
                        data-testid="expense-justificatif-input"
                      />
                      <p className="text-xs text-slate-500 mt-2">PDF, JPG, PNG (max 10 Mo) - Les images seront converties en PDF automatiquement</p>
                    </div>

                    {formData.justificatif && (
                      <>
                        <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded-lg">
                          <Paperclip className="w-4 h-4" />
                          <span className="font-medium flex-1 truncate">{formData.justificatif_filename}</span>
                          <Button type="button" variant="ghost" size="sm" onClick={removeFile} className="text-slate-400 h-6 w-6 p-0" title="Retirer le fichier">
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>

                        {/* Aperçu du fichier déposé */}
                        <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                          <div className="bg-slate-50 dark:bg-slate-800 px-3 py-2 text-xs font-medium text-slate-500 flex items-center gap-2">
                            <Eye className="w-3 h-3" /> Aperçu du justificatif
                          </div>
                          {renderFormPreview('h-64')}
                        </div>
                      </>
                    )}

                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                        Annuler
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setStep(2)}
                        disabled={!formData.justificatif}
                        className="text-slate-500"
                      >
                        Saisie manuelle
                      </Button>
                      <Button
                        onClick={handleExtract}
                        disabled={!formData.justificatif || extracting}
                        className="bg-[#0F5156] hover:bg-[#0d464a]"
                      >
                        {extracting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Analyse IA en cours...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Analyser avec l'IA
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Étape 2 : Vérification et soumission */}
                {step === 2 && (
                  <form onSubmit={handleSubmit} className="space-y-4 py-4">
                    {/* Aperçu du justificatif en miniature */}
                    {formData.justificatif ? (
                      <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                        <div className="bg-slate-50 dark:bg-slate-800 px-3 py-2 text-xs font-medium text-slate-500 flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <Paperclip className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{formData.justificatif_filename}</span>
                          </div>
                          <Button type="button" variant="ghost" size="sm" onClick={removeFile} className="text-slate-400 h-5 w-5 p-0" title="Retirer le fichier">
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                        {renderFormPreview('h-48')}
                      </div>
                    ) : (
                      <div className="border border-dashed border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 space-y-2">
                        <p className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-2">
                          <AlertTriangle className="w-3.5 h-3.5" /> Aucun justificatif joint : il est obligatoire pour déposer la note.
                        </p>
                        <Input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleFileChange}
                          className="file:mr-4 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-[#C27868] file:text-white hover:file:bg-[#a85d4d]"
                        />
                      </div>
                    )}

                    {/* Employé (si l'utilisateur a le droit de voir les employés) */}
                    {employees.length > 0 && canView && (
                      <div className="space-y-2">
                        <Label>Employé</Label>
                        <Select
                          value={formData.employee_id}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, employee_id: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Automatique (vous-même)" />
                          </SelectTrigger>
                          <SelectContent>
                            {employees.map((emp) => (
                              <SelectItem key={emp.id} value={emp.id}>
                                {emp.prenom} {emp.nom}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="montant_ttc">Montant TTC *</Label>
                        <Input
                          id="montant_ttc"
                          type="number"
                          step="0.01"
                          value={formData.montant_ttc}
                          onChange={(e) => setFormData(prev => ({ ...prev, montant_ttc: e.target.value }))}
                          placeholder="0.00"
                          required
                          data-testid="expense-amount-ttc-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="montant_ht">Montant HT</Label>
                        <Input
                          id="montant_ht"
                          type="number"
                          step="0.01"
                          value={formData.montant_ht}
                          onChange={(e) => setFormData(prev => ({ ...prev, montant_ht: e.target.value }))}
                          placeholder="0.00"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="tva">TVA</Label>
                        <Input
                          id="tva"
                          type="number"
                          step="0.01"
                          value={formData.tva}
                          onChange={(e) => setFormData(prev => ({ ...prev, tva: e.target.value }))}
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="categorie">Catégorie *</Label>
                        <Select
                          value={formData.categorie}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, categorie: value }))}
                        >
                          <SelectTrigger data-testid="expense-category-select">
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat.value} value={cat.value}>
                                {cat.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="date_depense">Date *</Label>
                        <Input
                          id="date_depense"
                          type="date"
                          value={formData.date_depense}
                          onChange={(e) => setFormData(prev => ({ ...prev, date_depense: e.target.value }))}
                          required
                          data-testid="expense-date-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="fournisseur">Fournisseur</Label>
                      <Input
                        id="fournisseur"
                        value={formData.fournisseur}
                        onChange={(e) => setFormData(prev => ({ ...prev, fournisseur: e.target.value }))}
                        placeholder="Nom du fournisseur"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Description *</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Description de la dépense..."
                        rows={2}
                        required
                        data-testid="expense-description-input"
                      />
                    </div>

                    <div className="flex justify-between gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setStep(1)}>
                        Retour
                      </Button>
                      <div className="flex gap-3">
                        <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                          Annuler
                        </Button>
                        <Button
                          type="submit"
                          className="bg-[#C27868] hover:bg-[#a85d4d]"
                          disabled={submitting}
                          data-testid="expense-submit-btn"
                        >
                          {submitting ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Envoi...
                            </>
                          ) : (
                            'Déposer la note de frais'
                          )}
                        </Button>
                      </div>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Confirmation doublon (au-dessus du formulaire, à côté de l'action) */}
      <AlertDialog open={duplicateDialogOpen} onOpenChange={(open) => { if (!open) setDuplicateDialogOpen(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Doublon potentiel détecté
            </AlertDialogTitle>
            <AlertDialogDescription>
              Une note de frais similaire existe déjà. Voulez-vous quand même déposer celle-ci ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {duplicates.map((dup, idx) => (
              <div key={dup.id || idx} className="text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-2 rounded flex items-center justify-between gap-2">
                <span className="truncate">
                  {formatDateShort(dup.date_depense)} - {dup.description || dup.categorie} - {formatCurrency(dup.montant_ttc)}
                  {dup.employee_name ? ` (${dup.employee_name})` : ''}
                </span>
                <Badge className={getStatusColor(dup.statut)} variant="secondary">{getStatusLabel(dup.statut)}</Badge>
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doSubmit} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="expense-submit-anyway-btn">
              Déposer quand même
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="card-stat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">En attente</p>
                <p className="text-2xl font-bold text-amber-500">{formatCurrency(totalPending)}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {expenses.filter(e => e.statut === 'en_attente' || e.statut === 'a_confirmer').length} note(s)
                </p>
              </div>
              <Receipt className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-stat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Approuvées</p>
                <p className="text-2xl font-bold text-emerald-500">{formatCurrency(totalApproved)}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {expenses.filter(e => e.statut === 'approuve').length} note(s)
                </p>
              </div>
              <Check className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-stat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total</p>
                <p className="text-2xl font-bold text-[#0F5156]">
                  {formatCurrency(expenses.reduce((sum, e) => sum + getMontant(e), 0))}
                </p>
                <p className="text-xs text-slate-400 mt-1">{expenses.length} note(s)</p>
              </div>
              <Euro className="w-8 h-8 text-[#0F5156]" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher par employé, catégorie, fournisseur..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="expense-search-input"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full sm:w-40" data-testid="expense-filter-status">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="en_attente">En attente</SelectItem>
                  <SelectItem value="a_confirmer">À confirmer</SelectItem>
                  <SelectItem value="approuve">Approuvées</SelectItem>
                  <SelectItem value="paye">Payées</SelectItem>
                  <SelectItem value="refuse">Refusées</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-48" data-testid="expense-sort">
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Trier par" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date_desc">Date (récent)</SelectItem>
                  <SelectItem value="date_asc">Date (ancien)</SelectItem>
                  <SelectItem value="montant_desc">Montant (décroissant)</SelectItem>
                  <SelectItem value="montant_asc">Montant (croissant)</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="flex items-center gap-2"
              >
                {showAdvancedFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                Filtres
              </Button>
            </div>

            {/* Filtre rapide : notes à traiter par moi */}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant={showMine ? 'default' : 'outline'}
                onClick={() => setShowMine(!showMine)}
                className={showMine ? 'bg-[#0F5156] hover:bg-[#0d464a] text-white' : ''}
                data-testid="expense-filter-mine"
              >
                <Inbox className="w-4 h-4 mr-2" />
                À traiter par moi ({toProcessCount})
              </Button>
              {showMine && (
                <span className="text-xs text-slate-500">Notes dont vous êtes le validateur à l'étape en cours, ou en attente de votre confirmation</span>
              )}
            </div>

            {showAdvancedFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <div className="space-y-2">
                  <Label className="text-sm text-slate-500 flex items-center gap-1">
                    <Calendar className="w-4 h-4" /> Date début
                  </Label>
                  <Input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-slate-500 flex items-center gap-1">
                    <Calendar className="w-4 h-4" /> Date fin
                  </Label>
                  <Input type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-slate-500 flex items-center gap-1">
                    <Euro className="w-4 h-4" /> Montant min
                  </Label>
                  <Input type="number" step="0.01" placeholder="0.00" value={montantMin} onChange={(e) => setMontantMin(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-slate-500 flex items-center gap-1">
                    <Euro className="w-4 h-4" /> Montant max
                  </Label>
                  <Input type="number" step="0.01" placeholder="0.00" value={montantMax} onChange={(e) => setMontantMax(e.target.value)} />
                </div>
                <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
                  <Button variant="ghost" size="sm" onClick={resetFilters} className="text-slate-500 hover:text-slate-700">
                    <RotateCcw className="w-4 h-4 mr-2" /> Réinitialiser
                  </Button>
                </div>
              </div>
            )}

            {(dateDebut || dateFin || montantMin || montantMax) && (
              <div className="flex flex-wrap gap-2 pt-2">
                {dateDebut && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Depuis : {dateDebut} <X className="w-3 h-3 cursor-pointer" onClick={() => setDateDebut('')} />
                  </Badge>
                )}
                {dateFin && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Jusqu'au : {dateFin} <X className="w-3 h-3 cursor-pointer" onClick={() => setDateFin('')} />
                  </Badge>
                )}
                {montantMin && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Min : {montantMin} € <X className="w-3 h-3 cursor-pointer" onClick={() => setMontantMin('')} />
                  </Badge>
                )}
                {montantMax && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Max : {montantMax} € <X className="w-3 h-3 cursor-pointer" onClick={() => setMontantMax('')} />
                  </Badge>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Split layout : liste à gauche, détail à droite */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Liste des notes de frais - GAUCHE */}
        <div className={`${detailExpense ? 'lg:w-1/2 xl:w-3/5' : 'w-full'} transition-all duration-300`}>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="table-header">Employé</TableHead>
                      <TableHead className="table-header">Date</TableHead>
                      <TableHead className="table-header">Catégorie</TableHead>
                      {!detailExpense && <TableHead className="table-header hidden md:table-cell">Description</TableHead>}
                      {!detailExpense && <TableHead className="table-header hidden lg:table-cell">Fournisseur</TableHead>}
                      <TableHead className="table-header">Montant TTC</TableHead>
                      <TableHead className="table-header">Statut</TableHead>
                      <TableHead className="table-header text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredExpenses.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={detailExpense ? 6 : 8} className="text-center py-8 text-slate-500">
                          Aucune note de frais trouvée
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredExpenses.map((expense) => (
                        <TableRow
                          key={expense.id}
                          className={`table-row cursor-pointer transition-colors ${detailExpense?.id === expense.id ? 'bg-[#0F5156]/5 dark:bg-[#0F5156]/10' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}
                          data-testid={`expense-row-${expense.id}`}
                          onClick={() => {
                            setDetailExpense(expense);
                            setActionComment('');
                            setEditDetail(expense.can_edit ? buildEditDetail(expense) : null);
                          }}
                        >
                          <TableCell className="table-cell">
                            <p className="font-medium">{expense.employee_name || '-'}</p>
                          </TableCell>
                          <TableCell className="table-cell">
                            <p className="text-sm">{formatDateShort(expense.date_depense)}</p>
                          </TableCell>
                          <TableCell className="table-cell capitalize">
                            {categories.find(c => c.value === expense.categorie)?.label || expense.categorie}
                          </TableCell>
                          {!detailExpense && (
                            <TableCell className="table-cell hidden md:table-cell">
                              <p className="truncate max-w-xs">{expense.description}</p>
                            </TableCell>
                          )}
                          {!detailExpense && (
                            <TableCell className="table-cell hidden lg:table-cell">
                              <p className="text-sm text-slate-500">{expense.fournisseur || '-'}</p>
                            </TableCell>
                          )}
                          <TableCell className="table-cell font-semibold">
                            <div>
                              <p>{formatCurrency(getMontant(expense))}</p>
                              {expense.montant_ht && (
                                <p className="text-xs text-slate-400">HT : {formatCurrency(expense.montant_ht)}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="table-cell">
                            <Badge className={getStatusColor(expense.statut)}>
                              {getStatusLabel(expense.statut)}
                            </Badge>
                            {(expense.statut === 'en_attente' || expense.statut === 'a_confirmer') && expense.etape_courante_label && (
                              <p className="text-xs text-slate-400 mt-1">{expense.etape_courante_label}</p>
                            )}
                          </TableCell>
                          <TableCell className="table-cell text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              {expense.has_justificatif && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                                  onClick={() => handlePreview(expense)}
                                  disabled={previewLoadingId === expense.id}
                                  title="Voir le justificatif"
                                  data-testid={`expense-preview-${expense.id}`}
                                >
                                  {previewLoadingId === expense.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                                </Button>
                              )}
                              {expense.statut === 'en_attente' && expense.is_assignee && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-emerald-600 hover:bg-emerald-50"
                                    onClick={() => handleStatusChange(expense.id, 'approuve')}
                                    disabled={actionLoading}
                                    title="Approuver"
                                  >
                                    <Check className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-600 hover:bg-red-50"
                                    onClick={() => handleStatusChange(expense.id, 'refuse')}
                                    disabled={actionLoading}
                                    title="Refuser"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                              {expense.statut === 'a_confirmer' && expense.can_confirm && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-sky-600 hover:bg-sky-50"
                                  onClick={() => handleStatusChange(expense.id, 'confirme')}
                                  disabled={actionLoading}
                                  title="Confirmer le montant corrigé"
                                >
                                  <ClipboardCheck className="w-4 h-4" />
                                </Button>
                              )}
                              {expense.statut === 'approuve' && canPay && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-[#0F5156] hover:bg-[#0F5156]/10"
                                  onClick={() => handleStatusChange(expense.id, 'paye')}
                                  disabled={actionLoading}
                                  title="Marquer comme payée"
                                >
                                  <Euro className="w-4 h-4" />
                                </Button>
                              )}
                              {expense.statut === 'en_attente' && (expense.is_submitter || canEditAny) && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-500 hover:bg-red-50"
                                  onClick={() => handleDelete(expense.id)}
                                  title="Supprimer"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Detail panel - DROITE */}
        {detailExpense && (
          <div className="lg:w-1/2 xl:w-2/5">
            <Card className="lg:sticky lg:top-4">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Détail de la note de frais</CardTitle>
                  <Button variant="ghost" size="icon" onClick={() => { setDetailExpense(null); setEditDetail(null); }} className="h-8 w-8">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                {/* Justificatif */}
                <div className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                  <Paperclip className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span className="text-sm flex-1 truncate">
                    {detailExpense.has_justificatif ? (detailExpense.justificatif_filename || 'Justificatif') : 'Aucun justificatif'}
                  </span>
                  {detailExpense.has_justificatif && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePreview(detailExpense)}
                        disabled={previewLoadingId === detailExpense.id}
                        data-testid="expense-detail-preview-btn"
                      >
                        {previewLoadingId === detailExpense.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                        Aperçu
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDownloadJustificatif(detailExpense)}>
                        <Download className="w-4 h-4 mr-2" />
                        Télécharger
                      </Button>
                    </>
                  )}
                </div>

                {/* Étape en cours / assignés */}
                {(detailExpense.statut === 'en_attente' || detailExpense.statut === 'a_confirmer') && (
                  <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 space-y-1">
                    <p className="text-xs font-medium text-amber-800 dark:text-amber-200 flex items-center gap-2">
                      <UserCheck className="w-3.5 h-3.5" />
                      Étape en cours : {detailExpense.etape_courante_label || 'Validation'}
                    </p>
                    {detailExpense.statut === 'en_attente' && (
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        Assignée à : {detailExpense.assignees_names?.length ? detailExpense.assignees_names.join(', ') : 'administrateurs'}
                      </p>
                    )}
                    {detailExpense.statut === 'a_confirmer' && (
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        {detailExpense.can_confirm
                          ? 'Le montant a été corrigé lors de la validation : merci de confirmer la note.'
                          : 'En attente de confirmation du montant corrigé par le déposant.'}
                      </p>
                    )}
                  </div>
                )}

                {/* Infos - éditables selon les règles du workflow (déposant à l'étape 0, assigné autorisé, admin) */}
                {editDetail && detailExpense.can_edit ? (
                  <>
                    <div className="flex items-center gap-2 mb-1">
                      <Pencil className="w-3.5 h-3.5 text-amber-500" />
                      <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                        Mode édition - {detailExpense.is_assignee ? 'vous pouvez corriger les champs avant validation' : 'vous pouvez encore corriger votre note'}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Employé</Label>
                        <p className="font-medium text-sm mt-0.5">{detailExpense.employee_name || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Date</Label>
                        <Input
                          type="date"
                          value={editDetail.date_depense}
                          onChange={(e) => setEditDetail({ ...editDetail, date_depense: e.target.value })}
                          className="h-8 text-sm mt-0.5"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Catégorie</Label>
                        <Select value={editDetail.categorie} onValueChange={(v) => setEditDetail({ ...editDetail, categorie: v })}>
                          <SelectTrigger className="h-8 text-sm mt-0.5">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map(c => (
                              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Fournisseur</Label>
                        <Input
                          value={editDetail.fournisseur}
                          onChange={(e) => setEditDetail({ ...editDetail, fournisseur: e.target.value })}
                          className="h-8 text-sm mt-0.5"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Montant TTC</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editDetail.montant_ttc}
                          onChange={(e) => setEditDetail({ ...editDetail, montant_ttc: e.target.value })}
                          className="h-8 text-sm mt-0.5 font-bold text-[#0F5156]"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Montant HT</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editDetail.montant_ht}
                          onChange={(e) => setEditDetail({ ...editDetail, montant_ht: e.target.value })}
                          className="h-8 text-sm mt-0.5"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">TVA</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editDetail.tva}
                          onChange={(e) => setEditDetail({ ...editDetail, tva: e.target.value })}
                          className="h-8 text-sm mt-0.5"
                        />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Statut</p>
                        <Badge className={getStatusColor(detailExpense.statut)}>{getStatusLabel(detailExpense.statut)}</Badge>
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs text-slate-500">Description</Label>
                      <Textarea
                        value={editDetail.description}
                        onChange={(e) => setEditDetail({ ...editDetail, description: e.target.value })}
                        className="mt-0.5 text-sm min-h-[60px]"
                      />
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => saveDetailEdits(true).catch(() => {})}
                      disabled={savingDetail}
                    >
                      {savingDetail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Sauvegarder les modifications
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-slate-500">Employé</p>
                        <p className="font-medium text-sm">{detailExpense.employee_name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Date</p>
                        <p className="font-medium text-sm">{formatDateShort(detailExpense.date_depense)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Catégorie</p>
                        <p className="font-medium text-sm capitalize">{categories.find(c => c.value === detailExpense.categorie)?.label || detailExpense.categorie}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Fournisseur</p>
                        <p className="font-medium text-sm">{detailExpense.fournisseur || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Montant TTC</p>
                        <p className="font-bold text-lg text-[#0F5156]">{formatCurrency(getMontant(detailExpense))}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Montant HT</p>
                        <p className="font-medium text-sm">{detailExpense.montant_ht ? formatCurrency(detailExpense.montant_ht) : '-'}</p>
                      </div>
                      {detailExpense.tva && (
                        <div>
                          <p className="text-xs text-slate-500">TVA</p>
                          <p className="font-medium text-sm">{formatCurrency(detailExpense.tva)}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-slate-500">Statut</p>
                        <Badge className={getStatusColor(detailExpense.statut)}>{getStatusLabel(detailExpense.statut)}</Badge>
                      </div>
                    </div>

                    <div>
                      <p className="text-xs text-slate-500">Description</p>
                      <p className="mt-1 text-sm">{detailExpense.description}</p>
                    </div>
                  </>
                )}

                {detailExpense.validateur_name && detailExpense.statut !== 'en_attente' && (
                  <div>
                    <p className="text-xs text-slate-500">Validée par</p>
                    <p className="font-medium text-sm">{detailExpense.validateur_name} - {formatDateShort(detailExpense.date_validation)}</p>
                  </div>
                )}

                {/* Corrections apportées */}
                {detailExpense.modifications && detailExpense.modifications.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2 flex items-center gap-1"><Pencil className="w-3 h-3" /> Corrections</p>
                    <div className="space-y-1">
                      {detailExpense.modifications.map((mod, idx) => (
                        <div key={idx} className="text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900 p-2 rounded">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium">{FIELD_LABELS[mod.champ] || mod.champ}</span>
                            <span className="text-slate-400">{mod.user_name} · {formatDateTime(mod.date)}</span>
                          </div>
                          <p className="text-slate-600 dark:text-slate-300 mt-0.5">
                            <span className="line-through">{formatFieldValue(mod.champ, mod.ancienne_valeur)}</span>
                            {' → '}
                            <span className="font-medium">{formatFieldValue(mod.champ, mod.nouvelle_valeur)}</span>
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Historique du workflow */}
                {detailExpense.workflow_history && detailExpense.workflow_history.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2 flex items-center gap-1"><History className="w-3 h-3" /> Historique</p>
                    <ol className="relative border-l border-slate-200 dark:border-slate-700 ml-2 space-y-3">
                      {detailExpense.workflow_history.map((entry, idx) => (
                        <li key={idx} className="ml-4">
                          <span className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full border-2 border-white dark:border-slate-900 bg-[#0F5156]"></span>
                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            <span className={`px-2 py-0.5 rounded-full font-medium ${ACTION_COLORS[entry.action] || ACTION_COLORS.soumis}`}>
                              {ACTION_LABELS[entry.action] || getStatusLabel(entry.action)}
                            </span>
                            {entry.step_label && <span className="text-slate-500">· {entry.step_label}</span>}
                            <span className="text-slate-600 dark:text-slate-300">{entry.user_name}</span>
                            <span className="text-slate-400 ml-auto">{formatDateTime(entry.date)}</span>
                          </div>
                          {(entry.commentaire || entry.comment) && (
                            <p className="text-xs text-slate-500 italic mt-1">{entry.commentaire || entry.comment}</p>
                          )}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* Actions de validation : uniquement pour les assignés de l'étape courante (ou admin) */}
                {detailExpense.statut === 'en_attente' && detailExpense.is_assignee && (
                  <div className="space-y-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                    <Textarea
                      value={actionComment}
                      onChange={(e) => setActionComment(e.target.value)}
                      placeholder="Commentaire (optionnel, transmis au déposant)"
                      className="text-sm min-h-[50px]"
                    />
                    <div className="flex gap-2">
                      <Button
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => handleStatusChange(detailExpense.id, 'approuve', actionComment)}
                        disabled={actionLoading}
                        data-testid="expense-detail-approve-btn"
                      >
                        {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} Approuver
                      </Button>
                      <Button
                        variant="destructive"
                        className="flex-1"
                        onClick={() => handleStatusChange(detailExpense.id, 'refuse', actionComment)}
                        disabled={actionLoading}
                        data-testid="expense-detail-reject-btn"
                      >
                        <X className="w-4 h-4 mr-2" /> Refuser
                      </Button>
                    </div>
                  </div>
                )}
                {detailExpense.statut === 'a_confirmer' && detailExpense.can_confirm && (
                  <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
                    <Button
                      className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                      onClick={() => handleStatusChange(detailExpense.id, 'confirme')}
                      disabled={actionLoading}
                      data-testid="expense-detail-confirm-btn"
                    >
                      <ClipboardCheck className="w-4 h-4 mr-2" /> Confirmer le montant corrigé
                    </Button>
                  </div>
                )}
                {detailExpense.statut === 'approuve' && canPay && (
                  <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
                    <Button
                      className="w-full bg-[#0F5156] hover:bg-[#0d464a]"
                      onClick={() => handleStatusChange(detailExpense.id, 'paye')}
                      disabled={actionLoading}
                    >
                      <Euro className="w-4 h-4 mr-2" /> Marquer comme payée
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Aperçu du justificatif (composant partagé, URL blob:) */}
      <DocumentPreviewDialog
        preview={previewDoc}
        onClose={closePreview}
        onDownload={previewDoc?.expense ? () => handleDownloadJustificatif(previewDoc.expense) : undefined}
      />
    </div>
  );
};

export default Expenses;
